// Weekly unavailability rows: day, start, end, note (SPEC §7.5 edit form).

import type { WeeklyUnavailable, Weekday } from "../api/types";
import { formatWeekdayShort, listWeekdays, t } from "../i18n/i18n";
import { CELLS_PER_DAY } from "../time";
import { createButton, createElement } from "./dom";
import { createSelect } from "./forms";
import { createTimeRangePicker, type TimeRangePicker } from "./timeRangePicker";

interface WeeklyRow {
  daySelect: HTMLSelectElement;
  picker: TimeRangePicker;
  note: HTMLInputElement;
  element: HTMLElement;
}

export interface WeeklyEditor {
  element: HTMLElement;
  getEntries: () => WeeklyUnavailable[];
}

export function createWeeklyEditor(entries: WeeklyUnavailable[]): WeeklyEditor {
  const rows: WeeklyRow[] = [];
  const list: HTMLDivElement = createElement("div");
  const addRow = (entry: WeeklyUnavailable): void => {
    const row: WeeklyRow = createRow(entry, (): void => {
      rows.splice(rows.indexOf(row), 1);
      row.element.remove();
    });
    rows.push(row);
    list.append(row.element);
  };
  entries.forEach(addRow);
  // A new row covers the whole day (00:00–24:00); narrow it when only part of the day is unavailable.
  const add: HTMLButtonElement = createButton(t("teachers.addWeekly"), (): void => {
    addRow({ day: "Monday", range: { start: 0, end: CELLS_PER_DAY }, note: null });
  });
  const element: HTMLDivElement = createElement("div", "slots-editor");
  element.append(createElement("span", "field-label", t("teachers.weeklyUnavailable")), list, add);
  return { element, getEntries: (): WeeklyUnavailable[] => rows.map(readRow) };
}

function createRow(entry: WeeklyUnavailable, onRemove: () => void): WeeklyRow {
  const options: { value: string; label: string }[] = listWeekdays().map(
    (day: string): { value: string; label: string } => ({ value: day, label: formatWeekdayShort(day) }));
  const daySelect: HTMLSelectElement = createSelect(options, entry.day);
  const picker: TimeRangePicker = createTimeRangePicker(entry.range);
  const note: HTMLInputElement = createElement("input");
  note.value = entry.note ?? "";
  note.placeholder = t("app.note");
  const element: HTMLDivElement = createElement("div", "slot-row");
  element.append(daySelect, picker.element, note, createButton(t("app.remove"), onRemove, "button-link"));
  return { daySelect, picker, note, element };
}

function readRow(row: WeeklyRow): WeeklyUnavailable {
  const note: string = row.note.value.trim();
  return { day: row.daySelect.value as Weekday, range: row.picker.getRange(), note: note === "" ? null : note };
}
